import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ccmApi } from '../lib/api';
import { useDraft, invalidateDraftCache } from '../lib/draft';
import { entryDateForMonth } from '../lib/month';
import { StatusPill } from '../components/status-pill.jsx';
import { ProgressTinybar } from '../components/progress-bar.jsx';
import { OverridesModal } from '../components/overrides-modal.jsx';
import { DraftAI } from '../components/draft-ai.jsx';
import { chronicCount } from '../lib/ai-suggest';
import { TimeEntryEditModal } from '../components/time-entry-edit-modal.jsx';
import { PushModal } from '../push-modal.jsx';
import { tasksForRoster } from '../lib/studio-tasks';
import { generateForPatient, pushAndSignForPatient, getPushEligibility, getWorkflowState, check99487Eligibility, checkTierBumpEligibilityBatch, writeCcmTierAffirmation, BN_COMBINED_KEY, checkBillLanded } from '../lib/push-emr';
import { ComplexCcmReviewModal } from '../components/complex-ccm-review-modal.jsx';
import { TierBumpReviewModal } from '../components/tier-bump-review-modal.jsx';
import { BhiNoAssessmentReviewModal } from '../components/bhi-no-assessment-review-modal.jsx';
import { PainNoScaleReviewModal } from '../components/pain-no-scale-review-modal.jsx';
import { BhiAssessmentHistoryModal } from '../components/bhi-assessment-history-modal.jsx';
import { StatusRail } from '../components/status-rail.jsx';
import { confirmDialog } from '../components/confirm-dialog.jsx';
import { programCardModel } from '../lib/program-card-model';
import { nextGenError } from '../lib/gen-error-state';
import { getPref } from '../lib/user-prefs';
import { entriesForMonth, sumMinutes } from '../lib/activity-log';
import {
  conditionLabel,
  targetForProgram,
  programLabelFor,
  normalizeProgram,
  isActivePatient,
  isWorkflowExcluded,
  studioOperationalForFilter,
  isEnrolledProgram,
  isBhiEnrolledPatient,
} from '../lib/workflow';
import { BHI_BN_KEY, resolveBhiBillCode, bhiGatesForCode, ccmSlotLabel, bhiMonthlyMinutes, resolveBhiBillCodeWithGates, _latestAssessmentForMonth } from '../lib/bhi-billing';
import { isPainEnrolledPatient } from '../lib/pain-billing';
import { pendingDecisions, decisionCounts, pendingBhiAck, pendingPainAck } from '../lib/decision-inbox';
import { rankCcmTiersByReimbursement } from '../lib/billing-generate';
import { safeAudit } from '../lib/consent-audit';
import { hasOpenAssessmentRisk, openRiskAssessmentTimestamps } from '../lib/assessment-risk';

// Inline init helper (was in hifi-data.jsx in the prototype)
function init(name) {
  return (name || '').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
}

const FOCUSABLE_DOCUMENT_TABS = new Set(['careplan', 'billing']);

function carePlanHeadingLabel(title) {
  const clean = String(title || '').trim().replace(/:$/, '');
  if (!clean) return '';
  return clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase();
}

function carePlanHeadingFor(line) {
  const clean = String(line || '').trim();
  if (!clean || clean.startsWith('-') || !clean.endsWith(':')) return '';
  const title = clean.slice(0, -1).trim();
  if (!title || title.length > 90 || title !== title.toUpperCase()) return '';
  return title;
}

function parseCarePlanDocument(text) {
  const lines = String(text || '').split(/\r?\n/);
  const sections = [];
  let current = { title: '', blocks: [] };
  const pushCurrent = () => {
    if (current.title || current.blocks.length) sections.push(current);
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    const heading = carePlanHeadingFor(line);
    if (heading) {
      pushCurrent();
      current = { title: heading, blocks: [] };
      continue;
    }

    const bullet = line.match(/^[-*]\s+(.+)$/);
    if (bullet) {
      current.blocks.push({ type: 'bullet', text: bullet[1].trim() });
      continue;
    }

    current.blocks.push({ type: 'paragraph', text: line });
  }

  pushCurrent();
  return sections.length ? sections : [{ title: '', blocks: [{ type: 'paragraph', text: String(text || '').trim() }] }];
}

function CarePlanDocument({ text }) {
  const sections = useMemo(() => parseCarePlanDocument(text), [text]);
  return (
    <div className="doc-text careplan-document" aria-label="Care plan document">
      {sections.map((section, sectionIdx) => {
        const groups = [];
        section.blocks.forEach((block) => {
          const last = groups[groups.length - 1];
          if (block.type === 'bullet') {
            if (last && last.type === 'list') last.items.push(block.text);
            else groups.push({ type: 'list', items: [block.text] });
          } else {
            groups.push({ type: 'paragraph', text: block.text });
          }
        });
        return (
          <section className="careplan-document-section" key={`${section.title || 'intro'}-${sectionIdx}`}>
            {section.title && <h4 className="careplan-section-title">{carePlanHeadingLabel(section.title)}</h4>}
            {groups.map((group, groupIdx) => group.type === 'list' ? (
              <ul className="careplan-list" key={`list-${groupIdx}`}>
                {group.items.map((item, itemIdx) => <li key={`${groupIdx}-${itemIdx}`}>{item}</li>)}
              </ul>
            ) : (
              <p className="careplan-paragraph" key={`p-${groupIdx}`}>{group.text}</p>
            ))}
          </section>
        );
      })}
    </div>
  );
}

function billingNoteHeadingFor(line) {
  const clean = String(line || '').trim();
  if (!clean || clean.startsWith('-')) return '';
  const sectionHeading = carePlanHeadingFor(clean);
  if (sectionHeading) return sectionHeading;
  if (clean.length <= 110 && /\bNOTE\b/i.test(clean) && !/[.;]$/.test(clean)) return clean;
  return '';
}

function parseBillingNoteDocument(text) {
  const lines = String(text || '').split(/\r?\n/);
  const sections = [];
  let current = { title: '', blocks: [] };
  const pushCurrent = () => {
    if (current.title || current.blocks.length) sections.push(current);
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    const heading = billingNoteHeadingFor(line);
    if (heading) {
      pushCurrent();
      current = { title: heading, blocks: [] };
      continue;
    }

    const meta = line.match(/^([A-Za-z][A-Za-z /.-]{1,32}):\s*(.+)$/);
    if (meta) {
      current.blocks.push({ type: 'meta', label: meta[1].trim(), value: meta[2].trim() });
      continue;
    }

    const bullet = line.match(/^[-*]\s+(.+)$/);
    if (bullet) {
      current.blocks.push({ type: 'bullet', text: bullet[1].trim() });
      continue;
    }

    current.blocks.push({ type: 'paragraph', text: line });
  }

  pushCurrent();
  return sections.length ? sections : [{ title: '', blocks: [{ type: 'paragraph', text: String(text || '').trim() }] }];
}

function BillingNoteDocument({ text }) {
  const sections = useMemo(() => parseBillingNoteDocument(text), [text]);
  return (
    <div className="doc-text billing-note-document" aria-label="Billing note document">
      {sections.map((section, sectionIdx) => {
        const groups = [];
        section.blocks.forEach((block) => {
          const last = groups[groups.length - 1];
          if (block.type === 'bullet') {
            if (last && last.type === 'list') last.items.push(block.text);
            else groups.push({ type: 'list', items: [block.text] });
          } else if (block.type === 'meta') {
            if (last && last.type === 'meta') last.items.push(block);
            else groups.push({ type: 'meta', items: [block] });
          } else {
            groups.push({ type: 'paragraph', text: block.text });
          }
        });

        return (
          <section className="billing-note-document-section" key={`${section.title || 'intro'}-${sectionIdx}`}>
            {section.title && <h4 className="billing-note-section-title">{section.title}</h4>}
            {groups.map((group, groupIdx) => {
              if (group.type === 'list') {
                return (
                  <ul className="billing-note-list" key={`list-${groupIdx}`}>
                    {group.items.map((item, itemIdx) => <li key={`${groupIdx}-${itemIdx}`}>{item}</li>)}
                  </ul>
                );
              }
              if (group.type === 'meta') {
                return (
                  <dl className="billing-note-meta" key={`meta-${groupIdx}`}>
                    {group.items.map((item, itemIdx) => (
                      <div className="billing-note-meta-row" key={`${item.label}-${itemIdx}`}>
                        <dt>{item.label}</dt>
                        <dd>{item.value}</dd>
                      </div>
                    ))}
                  </dl>
                );
              }
              return <p className="billing-note-paragraph" key={`p-${groupIdx}`}>{group.text}</p>;
            })}
          </section>
        );
      })}
    </div>
  );
}

function documentWordCount(text) {
  return String(text || '').trim().split(/\s+/).filter(Boolean).length;
}

function ManualDocumentEditor({ label, value, onChange, onSave, onCancel, saving, saveDisabled }) {
  return (
    <div className="manual-document-editor">
      <textarea
        aria-label={`${label} manual editor`}
        value={value}
        onChange={e => onChange(e.target.value)}
        autoFocus
        spellCheck
      />
      <div className="manual-document-editor-footer">
        <span className="manual-document-editor-meta">{documentWordCount(value)} words</span>
        <div className="manual-document-editor-actions">
          <button className="btn sm ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn sm accent" onClick={onSave} disabled={saveDisabled}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Patient adapter -------------------------------------------------------
// Mirrors the adapter pattern from time-logs.jsx. Surfaces the flat fields the
// Studio UI expects from the prototype's mock shape: name, dx[], target, logged,
// status, program, programLabel, consent, mrn, age, sex, avSlot, alerts.
// _raw holds the original data blob for PUT-back persistence.

// Sum minutes logged in the given YYYY-MM month for an activityLog array.
// Delegates to lib/activity-log so consolidated synthetic entries (added by
// the monthly push) are excluded from the sum.
function sumLoggedForMonth(activityLog, monthKey) {
  return sumMinutes(entriesForMonth(activityLog, monthKey));
}

function avSlotFor(id) {
  const tail = String(id || '').slice(-2);
  const n = parseInt(tail, 16);
  if (!Number.isFinite(n)) return 1;
  return (n % 6) + 1;
}

// Derive age from DOB (YYYY-MM-DD). Returns null if dob is missing/invalid.
function ageFromDob(dob) {
  if (!dob || typeof dob !== 'string') return null;
  const t = Date.parse(dob);
  if (!Number.isFinite(t)) return null;
  const now = Date.now();
  const years = (now - t) / (365.2425 * 24 * 3600 * 1000);
  if (years < 0 || years > 130) return null;
  return Math.floor(years);
}

// Derive a coarse status bucket from program + logged-vs-target.
// 'ready' = met target, 'short' = below target, 'apcm' = no minute target (flat fee).
function deriveStatus(target, logged) {
  if (target === 0) return 'apcm';
  if (logged >= target) return 'ready';
  return 'short';
}


function adaptPatient(row, monthKey) {
  if (!row || typeof row !== 'object') return null;
  const data = row.data && typeof row.data === 'object' ? row.data : row;
  const fname = data.fname || '';
  const lname = data.lname || '';
  const name = `${fname} ${lname}`.trim() || data.name || data.mrn || row.id;
  const program = data.billing?.program || '';
  const conditions = Array.isArray(data.conditions) ? data.conditions : [];
  const dx = conditions.map(conditionLabel).filter(Boolean);
  const consent = data.consent || 'pending';
  const target = targetForProgram(program);
  const logged = sumLoggedForMonth(data.activityLog, monthKey);
  const status = deriveStatus(target, logged);
  return {
    id: row.id,
    name,
    mrn: data.mrn || '',
    dx,
    target,
    logged,
    status,
    // Canonical program string: '' for not-enrolled, otherwise UPPER-CASED.
    // Downstream code should check `isEnrolledProgram(p.program)` rather
    // than comparing to legacy `'none'`.
    program: normalizeProgram(program),
    programLabel: programLabelFor(program),
    consent,
    age: ageFromDob(data.dob),
    sex: data.sex || '',
    avSlot: avSlotFor(row.id),
    alerts: Array.isArray(data.alerts) ? data.alerts.length : (Number(data.alerts) || 0),
    // Workflow-eligibility flags derived from the raw Elation-synced blob.
    // Mirror Time Logs' adaptPatient so the same predicates apply across pages.
    active: isActivePatient(data),
    workflowExcluded: isWorkflowExcluded(data),
    _raw: data,
  };
}

// --- Inline text highlighter (used by billing summary) ---------------------
function highlightText(text, highlights) {
  if (!text) return null;
  if (!highlights || !highlights.length) return text;
  const parts = [];
  let cur = text;
  highlights.forEach(h => {
    const idx = cur.indexOf(h);
    if (idx === -1) return;
    parts.push(cur.slice(0, idx));
    parts.push(<span className="hl" key={h + idx}>{h}</span>);
    cur = cur.slice(idx + h.length);
  });
  parts.push(cur);
  return parts;
}

// --- Push button anchor (portal into chrome's #push-anchor) ----------------
// Single chrome-bar button whose label reflects the next workflow action:
//   - "Generate N"      — when N patients are eligible-not-yet-generated
//   - "Push & Sign N"   — when no patients need generation and M have drafts
//                          ready to ship
// The label is computed by the parent; this component just renders.
function PushAnchor({ count, onPush, isNarrow, month, label }) {
  const el = typeof document !== 'undefined' ? document.getElementById('push-anchor') : null;
  if (!el) return null;
  const shortMonth = (typeof month === 'string' ? month.split(' ')[0].slice(0, 3) : '');
  const inner = isNarrow
    ? <>{label} <span className="count tabnum">{count}</span> · {shortMonth}</>
    : <>{label} <span className="count tabnum">{count}</span> · {month} to EMR →</>;
  return ReactDOM.createPortal(
    <button className="btn push" disabled={count === 0} onClick={onPush}>
      {inner}
    </button>,
    el
  );
}

// --- The page --------------------------------------------------------------
function staffNameForUser(user) {
  return String(user?.displayName || user?.name || user?.email || '').trim();
}

export function StudioPage({ month, onJumpToTimePatient, onJumpToApcmPatient, focusPatient, onFocusConsumed, hideNoChronic, currentUser, programFilter, setProgramFilter }) {
  const canManagePain = ['admin', 'provider'].includes(String(currentUser?.role || '').toLowerCase());
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState(() => getPref('defaultReviewTab'));
  const [toast, setToast] = useState(null);
  // Billing review gates that were SKIPPED because their eligibility lookup
  // failed (fail-open by design). Durable + dismissible — a toast here gets
  // clobbered by the generate flow's own toasts moments later.
  const [gateNotices, setGateNotices] = useState([]);
  const [settingsFor, setSettingsFor] = useState(null);
  const [showPush, setShowPush] = useState(false);
  const [billingSub, setBillingSub] = useState('ccm');
  const lastBillingPatient = useRef(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [mobileView, setMobileView] = useState('roster'); // 'roster' | 'preview' (only at ≤880px)
  const [bandExpanded, setBandExpanded] = useState(false);
  const [documentFocus, setDocumentFocus] = useState(false);
  const [resumeFrom, setResumeFrom] = useState(null);
  const [resumeDismissed, setResumeDismissed] = useState(false);
  const [scrollPct, setScrollPct] = useState(0);
  const [monthLabel, setMonthLabel] = useState('');
  // Per-patient busy flag for the preview header's Generate button.
  // Keyed by `${patientId}:${month}` so the spinner follows the active month.
  const [generatingPatients, setGeneratingPatients] = useState({});
  // Persistent per-(patient,month) generate-failure markers. A failed AI
  // generation used to only flash a ~5s toast; this keeps it visible on the
  // card until the patient succeeds or the clinician retries.
  const [genErrors, setGenErrors] = useState({});
  // Per-patient busy flag for the preview header's Push & Sign button.
  const [pushSignPatients, setPushSignPatients] = useState({});
  // Bump on successful generate so useDraft() re-runs and picks up the new plan.
  const [draftRefreshKey, setDraftRefreshKey] = useState(0);
  const [reviewingRisk, setReviewingRisk] = useState({}); // { [patientId]: true } while the at-risk review POST is in flight
  // The activityLog entry currently being edited via the inline modal; null
  // hides the modal. Set by clicking Edit on a Time-log row in the preview.
  const [editingEntry, setEditingEntry] = useState(null);
  // Cached billedState so getPushEligibility can flag "already submitted"
  // patients without re-fetching per render. Refreshed after a successful
  // push from the preview-header button or after the bulk modal closes.
  const [billedState, setBilledState] = useState({});
  // Cached billing-notes map keyed by patientId → month. Drives the workflow
  // state derivation (needsGenerate vs. needsPushSign) without a per-render
  // refetch. Refreshed alongside billedState on month change + after each
  // successful Generate / Push & Sign.
  const [billingNotesMap, setBillingNotesMap] = useState({});
  // 99487 Complex CCM pre-generation review state. The check fires at
  // Generate time (when the clinical document is being created) rather than
  // at Push time — that's when the substantial-revision decision actually
  // lands in the plan.
  const [complexCcmReview, setComplexCcmReview] = useState(null);
  const [complexCcmBusy, setComplexCcmBusy] = useState(false);
  const [tierBumpReview, setTierBumpReview] = useState(null); // { rows:[oneRow], patient, mStr, forceRerun, program }
  const [tierBumpBusy, setTierBumpBusy] = useState(false);
  // BHI SOFT-guard override: when the operator triggers Generate / Push & Sign
  // for a BHI patient with NO initial assessment on file, we hold behind an
  // explicit confirmation modal before threading { assessmentOverride: true }.
  // Shape: { patient, mStr, mode:'generate'|'push', forceRerun? }.
  const [bhiNoAssessmentReview, setBhiNoAssessmentReview] = useState(null);
  const [bhiNoAssessmentBusy, setBhiNoAssessmentBusy] = useState(false);
  // CPM missing-scale SOFT guard. Pain surfaces remain provider/admin-only;
  // the acknowledgement is threaded through the combined CCM generate/push.
  const [painNoScaleReview, setPainNoScaleReview] = useState(null);
  const [painNoScaleBusy, setPainNoScaleBusy] = useState(false);
  const [painStaffList, setPainStaffList] = useState([]);
  // Decisions inbox (provider/admin). inboxTierReview reuses TierBumpReviewModal
  // with the same rows contract; keptStandard is SESSION-LOCAL — "Keep 99490"
  // stores nothing by design (writeCcmTierAffirmation deletes on empty affirm),
  // so the row is hidden for this session and re-enumerates next visit until
  // the month is billed.
  const [inboxTierReview, setInboxTierReview] = useState(null); // { rows:[oneRow], patient, mStr }
  const [inboxTierBusy, setInboxTierBusy] = useState(false);
  const [inboxKeptStandard, setInboxKeptStandard] = useState(() => new Set()); // `${patientId}:${month}` keys
  const [historyOpen, setHistoryOpen] = useState(false);
  const [manualEdit, setManualEdit] = useState(null);
  const [manualEditText, setManualEditText] = useState('');
  const [manualSaving, setManualSaving] = useState(false);

  const rosterListRef = useRef(null);
  const sectionRefs = useRef({});
  const swipeStart = useRef(null);
  // Synchronous in-flight set for generation. setGeneratingPatients is async +
  // React-batched, so two fast clicks can both pass a state-based guard before
  // the first flushes; a ref is updated synchronously and closes that window.
  const generatingRef = useRef(new Set());

  // Load patients (and reload on month change so logged totals follow the active month).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ccmApi.listPatients().then(rows => {
      if (cancelled) return;
      const adapted = (rows || []).filter(r => studioOperationalForFilter(r, programFilter)).map(r => adaptPatient(r, month)).filter(Boolean);
      setPatients(adapted);
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setPatients([]);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [month, programFilter]);

  useEffect(() => {
    if (!canManagePain) {
      setPainStaffList([]);
      return undefined;
    }
    let cancelled = false;
    ccmApi.listStaff().then((list) => {
      if (!cancelled) setPainStaffList(Array.isArray(list) ? list : []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [canManagePain]);

  // Load billedState + billing-notes map so the chrome-bar count + preview
  // header buttons can derive which action is next (Generate vs Push & Sign)
  // without per-render network calls.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      ccmApi.getBilledState().catch(() => ({})),
      ccmApi.listBillingNotes().catch(() => ({})),
    ]).then(([bs, bn]) => {
      if (cancelled) return;
      setBilledState(bs || {});
      const map = (bn && bn.billing_notes) ? bn.billing_notes : (bn || {});
      setBillingNotesMap(map || {});
    });
    return () => { cancelled = true; };
  }, [month, draftRefreshKey]);

  // Compute the human-readable month string from the YYYY-MM key for the push button.
  useEffect(() => {
    if (!month) { setMonthLabel(''); return; }
    const [y, m] = month.split('-').map(Number);
    if (!Number.isFinite(y) || !Number.isFinite(m)) { setMonthLabel(''); return; }
    const d = new Date(y, m - 1, 1);
    setMonthLabel(d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
  }, [month]);

  function getLogged(p) { return p.logged ?? 0; }
  function getApcmDecisionForMonth(p) {
    return (month && p?._raw?.apcmMonth?.[month]) || null;
  }
  function getLiveStatus(p) {
    // APCM-decided rows surface as 'apcm' regardless of minutes — billing is
    // a flat per-month G-code, not the CCM minute threshold.
    if (getApcmDecisionForMonth(p)) return 'apcm';
    if (!p.target) return 'apcm';
    const l = getLogged(p);
    if (l >= p.target) return 'ready';
    return 'short';
  }

  const canEditDocuments = ['admin', 'provider'].includes(currentUser?.role);

  function startManualEdit(section, opts) {
    if (!canEditDocuments) return;
    const options = opts || {};
    setManualEdit({
      section,
      program: options.program || 'ccm',
      sourceText: options.text || '',
      noteTypes: options.noteTypes || [],
    });
    setManualEditText(options.text || '');
  }

  function cancelManualEdit() {
    setManualEdit(null);
    setManualEditText('');
    setManualSaving(false);
  }

  async function saveManualDocument() {
    if (!manualEdit || !patient?.id || !month || manualSaving) return;
    const nextText = manualEditText.trim();
    if (!nextText) return;

    setManualSaving(true);
    try {
      let documentKey = 'care_plan';
      if (manualEdit.section === 'careplan') {
        await ccmApi.saveCarePlan(patient.id, month, nextText);
        invalidateDraftCache?.(patient.id, month);
      } else if (manualEdit.section === 'billing') {
        const saveType = manualEdit.program === 'bhi' ? BHI_BN_KEY : BN_COMBINED_KEY;
        documentKey = saveType;
        await ccmApi.saveCarePlan(patient.id, month, nextText, saveType);
        const oldTypes = Array.isArray(manualEdit.noteTypes) ? manualEdit.noteTypes : [];
        const cleanup = oldTypes
          .filter(t => t && t !== saveType)
          .map(t => ccmApi.deleteCarePlan(patient.id, month, t));
        if (cleanup.length) await Promise.all(cleanup);
        invalidateDraftCache?.(patient.id, month, manualEdit.program || 'ccm');
      } else {
        throw new Error('Unknown document type.');
      }
      ccmApi.saveDocumentEditTelemetry(patient.id, month, {
        document_type: manualEdit.section === 'careplan' ? 'care_plan' : 'billing_note',
        document_key: documentKey,
        program: manualEdit.program || 'ccm',
        edit_kind: manualEdit.sourceText ? 'manual_edit' : 'manual_create',
        source_note_types: manualEdit.noteTypes || [],
        before_text: manualEdit.sourceText || '',
        after_text: nextText,
      }).catch(() => {});
      setDraftRefreshKey(k => k + 1);
      setToast({
        id: Date.now(),
        text: `${manualEdit.section === 'careplan' ? 'Care plan' : 'Billing note'} updated for ${patient.name}`,
        undo: null,
      });
      cancelManualEdit();
    } catch (err) {
      setToast({
        id: 'manual-edit-' + Date.now(),
        text: `Save failed: ${err?.message || 'request failed'}`,
        undo: null,
        error: true,
      });
      setManualSaving(false);
    }
  }

  // Bookmark — save the active patient, restore on mount within the same
  // browser session. sessionStorage (not localStorage) so the resume hint
  // doesn't outlive the browser/tab close — on shared workstations the next
  // operator shouldn't pick up a banner pointing to the previous user's last
  // patient. The stored value is an internal patient UUID, not PHI, but
  // dropping it on close is the conservative default. v3-specific key so we
  // don't collide with the legacy /v2 prototype.
  useEffect(() => {
    if (selectedId) {
      try { sessionStorage.setItem('ccm.v3.studio.lastPatient', selectedId); } catch (_) {}
    }
  }, [selectedId]);
  useEffect(() => {
    if (!patients.length) return;
    try {
      const last = sessionStorage.getItem('ccm.v3.studio.lastPatient');
      if (last && patients.find(p => p.id === last)) setResumeFrom(last);
      // One-time cleanup: clear any stale localStorage value left by previous
      // builds so the bookmark doesn't leak between sessions on devices that
      // already have it persisted.
      try { localStorage.removeItem('ccm.v3.studio.lastPatient'); } catch (_) {}
    } catch (_) {}
  }, [patients]);

  // Auto-dismiss the resume banner when the user picks a different patient
  // from the roster. Without this, the banner sits there until the user
  // explicitly clicks it or the ✕.
  useEffect(() => {
    if (selectedId && resumeFrom && selectedId !== resumeFrom) {
      setResumeDismissed(true);
    }
  }, [selectedId]);

  // Track scroll position of the roster list for the rail thumb.
  useEffect(() => {
    const el = rosterListRef.current;
    if (!el) return;
    const onScroll = () => {
      const max = el.scrollHeight - el.clientHeight;
      setScrollPct(max > 0 ? el.scrollTop / max : 0);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
    // The roster-list element is rendered unconditionally (skeletons render
    // inside it), so the ref is stable — attach once on mount. An empty dep
    // array stops this from re-binding the listener on every render.
  }, []);

  function jumpToSection(key) {
    const el = sectionRefs.current[key];
    const list = rosterListRef.current;
    if (el && list) list.scrollTo({ top: Math.max(0, el.offsetTop - 4), behavior: 'smooth' });
  }

  // Detect narrow screens for master/detail layout.
  // We use a media query (≤880px) only — the data-ipad attribute / dev toggle
  // belongs to Task 21 (auto-detect). The narrow layout itself works without it.
  useEffect(() => {
    const check = () => setIsNarrow(window.innerWidth <= 880);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  useEffect(() => {
    if (!FOCUSABLE_DOCUMENT_TABS.has(tab)) setDocumentFocus(false);
  }, [tab]);
  useEffect(() => {
    cancelManualEdit();
  }, [selectedId, month, tab, billingSub]);

  // Deep-link from Time Logs' "Open patient detail" (›) button: when
  // focusPatient changes, select that patient in the preview and clear the
  // focus hint via onFocusConsumed so repeated clicks on the same row still
  // retrigger. `ts` on focusPatient guards against React skipping renders
  // when the id is unchanged.
  useEffect(() => {
    if (!focusPatient?.id) return;
    setSelectedId(focusPatient.id);
    if (isNarrow) setMobileView('preview');
    onFocusConsumed?.();
  }, [focusPatient?.id, focusPatient?.ts]);

  const filtered = useMemo(() => {
    return patients.filter(p => {
      if (hideNoChronic && chronicCount(p) === 0) return false;
      if (query) {
        const q = query.toLowerCase();
        if (!(p.name || '').toLowerCase().includes(q) && !(p.mrn || '').toLowerCase().includes(q)) return false;
      }
      if (filter === 'all')   return true;
      if (filter === 'ready') return getLiveStatus(p) === 'ready';
      if (filter === 'short') return getLiveStatus(p) === 'short';
      return true;
    });
  }, [patients, query, filter, hideNoChronic]);

  const patient = useMemo(
    () => patients.find(p => p.id === selectedId) || null,
    [patients, selectedId]
  );

  function patchPatientRaw(id, nextRaw) {
    setPatients(prev => prev.map(p => {
      if (p.id !== id) return p;
      const re = adaptPatient({ id: p.id, data: nextRaw }, month);
      return re || { ...p, _raw: nextRaw };
    }));
  }

  function patchPatientFromEligibility(payload) {
    const nextRaw = payload?.patient?.data;
    if (!patient || !nextRaw || typeof nextRaw !== 'object') return;
    patchPatientRaw(patient.id, nextRaw);
    invalidateDraftCache?.();
    setDraftRefreshKey(k => k + 1);
  }

  // Reset billingSub to match programFilter when the selected patient changes.
  // Persists across programFilter changes so operators can flip the chip
  // without losing their sub-tab choice within a patient. Must follow the
  // `patient` declaration above — referencing it earlier triggers a TDZ
  // ReferenceError on render (regression from PR #131).
  useEffect(() => {
    if (lastBillingPatient.current !== patient?.id) {
      lastBillingPatient.current = patient?.id;
      setBillingSub(programFilter === 'bhi' ? 'bhi' : 'ccm');
    }
  }, [patient?.id, programFilter]);
  // useDraft must be called unconditionally (Rules of Hooks). When patient is
  // null it returns the empty default, which we simply don't render below.
  const ccmDraft = useDraft(patient, month, draftRefreshKey, 'ccm');
  const bhiDraft = useDraft(patient, month, draftRefreshKey, 'bhi');
  // Care plan + Time log tabs read from the CCM draft (unified narrative; see
  // lib/draft.js program-partitioning comments). Aliased here so existing
  // references below ('draft.careplan', 'draft.timelog') keep working.
  const draft = ccmDraft;

  // Group filtered roster by live status — used for "All" sectioned view.
  const groups = useMemo(() => {
    const short = [], ready = [], apcm = [];
    filtered.forEach(p => {
      const apcmDecision = month && p._raw?.apcmMonth?.[month];
      // APCM fallback section: patients with a recorded APCM tier for this month.
      // Takes precedence over Ready/Short so the operator sees the decision
      // grouped explicitly rather than buried in the Ready section.
      if (apcmDecision) { apcm.push({ ...p, apcmDecision }); return; }
      const s = getLiveStatus(p);
      if (s === 'short') short.push(p);
      else ready.push(p);
    });
    return { short, ready, apcm };
  }, [filtered, month]);

  const resumePatient = resumeFrom && !resumeDismissed && resumeFrom !== selectedId
    ? patients.find(p => p.id === resumeFrom) : null;

  function selectPatient(id) {
    setSelectedId(id);
    if (isNarrow) setMobileView('preview');
  }

  // True when a BHI action for (p, mStr) is blocked SOLELY because no initial
  // assessment is on file — i.e., overriding the assessment gate would make
  // gates pass, but they don't pass today. Enrollment/consent/minute failures
  // return false here (those are hard and handled by the normal skip/throw).
  // Delegates to decision-inbox.js so the Decisions inbox and the generate-
  // time gate can never disagree on what is pending.
  function bhiNeedsAssessmentOverride(p, mStr) {
    return pendingBhiAck(p?._raw || p || {}, mStr);
  }

  // True only when the missing in-month NRS is the sole CPM blocker. The
  // override must make the same gate pass; no consent, record, or minute
  // failure can reach the acknowledgement modal. Same single source of truth
  // as the Decisions inbox (decision-inbox.js); the role gate stays here.
  function painNeedsScaleAck(p, mStr) {
    if (!canManagePain) return false;
    return pendingPainAck(p?._raw || p || {}, mStr, painStaffList);
  }

  // Two-stage per-patient workflow:
  //   Stage 1 — handleGenerate: care plan + billing note in CCM only.
  //   (Provider reviews in the preview tabs.)
  //   Stage 2 — handlePushAndSign: ship to Elation + sign + mark billed.
  //
  // For 99487 (Complex CCM) patients with no new labs/notes since the prior
  // plan, Generate is gated behind a pre-generation review modal — the CMS
  // substantial-revision requirement is at risk and the operator must decide
  // whether to proceed, downgrade to 99490/99491, or cancel before any AI
  // content gets written.
  async function handleGenerate(p, mStr, opts) {
    setGateNotices([]); // new run — clear prior skipped-gate warnings
    const key = `${p.id}:${mStr}`;
    if (generatingPatients[key]) return;
    const ws = getWorkflowState(p, mStr, billedState, billingNotesMap, opts || {});
    // forceRerun is implied whenever an existing billing note is present —
    // operator's pressed Generate again to overwrite. Also implies clearing
    // the billed flag (handled in generateForPatient).
    const forceRerun = ws.hasBillingNote;
    if (forceRerun) {
      const ok = await confirmDialog({
        title: `Regenerate for ${p.name}?`,
        body: ws.isBilled
          ? `This replaces the existing plan & billing note for ${p.name} and clears the submitted status.`
          : `This replaces ${p.name}'s existing draft plan & billing note.`,
        confirmLabel: 'Regenerate',
        danger: true,
      });
      if (!ok) return;
    } else if (getPref('confirmBeforePush')) {
      const ok = await confirmDialog({
        title: `Generate for ${p.name}?`,
        body: `Generate this month's care plan and billing note for ${p.name}.`,
        confirmLabel: 'Generate',
      });
      if (!ok) return;
    }

    await runPreGenerateGates(p, mStr, { forceRerun, program: (opts || {}).program });
  }

  // Pre-generate gate chain. Every entry point into generation goes through
  // here; review-modal continuations re-enter with their own gate marked
  // passed so every gate BELOW them still runs (a continuation must never
  // skip a review it hasn't shown).
  //   gateOpts: { program, forceRerun, freshness, skipTierBump, skip99487 }
  async function runPreGenerateGates(p, mStr, gateOpts) {
    gateOpts = gateOpts || {};
    const { program, forceRerun } = gateOpts;
    // CCM tier auto-bump check (Standard-CCM patients at >= 90 min). Reuse the
    // batch with a single patient so eligibility + physician-% hint are computed
    // in one place. Fail-open on lookup error. CCM-only: a BHI generate of a
    // 99490-enrolled patient could still match checkTierBumpEligibilityBatch
    // (it filters to 99490-CCM, not by the requested program), so guard it.
    if (!gateOpts.skipTierBump && program !== 'bhi') {
      try {
        const bumpMap = await checkTierBumpEligibilityBatch([p], mStr);
        const info = bumpMap.get(p.id);
        if (info) {
          setTierBumpReview({
            // existingCode threads the prior same-month ccmTierMonth override so
            // the modal seeds the matching radio (stale bump renders checked;
            // down-selecting to "Keep 99490" emits an explicit clear).
            rows: [{ patientId: p.id, name: p.name, minutes: info.minutes, physicianPct: info.physicianPct, existingCode: info.existingCode || null }],
            patient: p, mStr, forceRerun, program,
          });
          return;
        }
      } catch (_) {
        // Fail-open by design, but never silently: the operator should know a
        // billing review gate was skipped because of a lookup error.
        setGateNotices((w) => [...w, `Couldn't check tier-bump eligibility for ${p.name} — generated without that review.`]);
      }
    }

    // 99487 review is CCM-only: a BHI generate writes no CCM care plan
    // (push-emr.js generateForPatient BHI branch), so the CMS substantial-
    // revision review belongs to the CCM leg. push-modal.jsx applies the same
    // scope to both pre-flights.
    if (!gateOpts.skip99487 && program !== 'bhi') {
      let eligibility = null;
      try { eligibility = await check99487Eligibility(p, mStr); }
      catch (_) {
        setGateNotices((w) => [...w, `Couldn't check 99487 eligibility for ${p.name} — generated without that review.`]);
      }
      if (eligibility) {
        setComplexCcmReview({ patient: p, monthStr: mStr, eligibility, forceRerun, program });
        return;
      }
    }

    // BHI SOFT-guard: if the only thing blocking this BHI generate is a missing
    // initial assessment, require an explicit, warned confirmation before we
    // thread { assessmentOverride: true }. Other hard blockers (consent,
    // minutes, enrollment) fall through and the generate skips with that reason.
    if (program === 'bhi' && bhiNeedsAssessmentOverride(p, mStr)) {
      setBhiNoAssessmentReview({ patient: p, mStr, mode: 'generate', forceRerun });
      return;
    }

    await _runGenerate(p, mStr, gateOpts.freshness || null, { forceRerun, program });
  }

  async function _runGenerate(p, mStr, freshness, runOptions) {
    const opts = runOptions || {};
    // CPM rides the combined CCM/APCM bill. Hold every CCM generation entry
    // point — including tier/complex-review continuations — behind the same
    // missing-scale acknowledgement before any note is written.
    if ((opts.program || 'ccm') !== 'bhi'
        && opts.painScaleOverride !== true
        && painNeedsScaleAck(p, mStr)) {
      setPainNoScaleReview({ patient: p, mStr, mode: 'generate', forceRerun: opts.forceRerun === true, freshness });
      return;
    }
    const key = `${p.id}:${mStr}`;
    // Synchronous double-submit guard: bail if a generation for this key is
    // already in flight (immune to React's async state batching).
    if (generatingRef.current.has(key)) return;
    generatingRef.current.add(key);
    setGeneratingPatients((prev) => ({ ...prev, [key]: true }));
    setGenErrors((prev) => nextGenError(prev, key, { clear: true }));
    const tid = 'gen-' + Date.now();
    setToast({ id: tid, text: `Generating ${p.name}'s draft…`, undo: null });
    try {
      const res = await generateForPatient(p, mStr, {
        freshness: freshness || undefined,
        forceRerun: opts.forceRerun === true,
        program: opts.program || undefined,
        assessmentOverride: opts.assessmentOverride === true,
        painScaleOverride: opts.painScaleOverride === true,
        onStep: ({ step }) => {
          const labels = {
            'careplan':     `${p.name}: generating care plan…`,
            'billing-note': `${p.name}: generating billing note…`,
          };
          if (labels[step]) setToast({ id: tid, text: labels[step], undo: null });
        },
      });
      if (res.skipped) {
        setToast({ id: tid, text: `${p.name} skipped — ${res.reason}`, undo: null, error: true });
        setTimeout(() => setToast(t => (t && t.id === tid ? null : t)), 4500);
        setGenErrors((prev) => nextGenError(prev, key, { skipped: true }));
      } else if (res.ok) {
        setToast({ id: tid, text: `Draft ready for ${p.name} — review, then Push & Sign`, undo: null });
        setTimeout(() => setToast(t => (t && t.id === tid ? null : t)), 3500);
        setGenErrors((prev) => nextGenError(prev, key, { ok: true }));
        setDraftRefreshKey(k => k + 1);
      } else {
        const msg = res.errors.join('; ') || 'generate failed';
        setToast({ id: tid, text: `Generate failed: ${msg}`, undo: null, error: true });
        setTimeout(() => setToast(t => (t && t.id === tid ? null : t)), 6000);
        setGenErrors((prev) => nextGenError(prev, key, { message: msg }));
      }
    } catch (err) {
      const errId = 'err' + Date.now();
      const msg = err?.message || 'Generate failed';
      setToast({ id: errId, text: msg, undo: null, error: true });
      setTimeout(() => setToast(t => (t && t.id === errId ? null : t)), 5000);
      setGenErrors((prev) => nextGenError(prev, key, { message: msg }));
    } finally {
      generatingRef.current.delete(key);
      setGeneratingPatients((prev) => { const n = { ...prev }; delete n[key]; return n; });
    }
  }

  // Invoked by TierBumpReviewModal once the operator affirms (or declines) the
  // higher tier for a single patient. Persists the per-month override (if any),
  // then re-enters the remaining pre-generate gates. Tier-bump is CCM-only,
  // but the requested program stays explicit across the continuation.
  async function applyTierBumpAndGenerate(affirmByPatient) {
    const ctx = tierBumpReview;
    if (!ctx || tierBumpBusy) return;
    const affirm = affirmByPatient && affirmByPatient[ctx.patient.id];
    let patientToRun = ctx.patient;
    // Persist whenever an affirm entry exists. The modal seeds an explicit
    // entry for every displayed row (from the prior ccmTierMonth override), so
    // on Apply the entry is always present; a both-false entry means "Keep
    // 99490" (chosen or left seeded) and we STILL call
    // writeCcmTierAffirmation({}) so any PRIOR same-month 99487/99491 override in
    // ccmTierMonth[month] is CLEARED (an empty affirm deletes the entry; it
    // no-ops when nothing was stored) — else a stale override would still bill
    // the higher tier despite down-selecting. No entry = defensive guard only
    // (e.g. a legacy caller passing a partial map) -> leave state as-is.
    if (affirm) {
      setTierBumpBusy(true);
      const row = ctx.rows[0] || {};
      try {
        patientToRun = await writeCcmTierAffirmation(ctx.patient, ctx.mStr, affirm, {
          affirmedBy: 'operator', minutes: row.minutes, physicianPct: row.physicianPct,
        });
      } catch (e) {
        setToast({ id: 'tb-err' + Date.now(), text: 'Tier affirmation failed: ' + (e?.message || 'unknown'), undo: null, error: true });
        setTierBumpBusy(false);
        return;
      }
      setTierBumpBusy(false);
    }
    setTierBumpReview(null);
    await runPreGenerateGates(patientToRun, ctx.mStr, { program: ctx.program, forceRerun: ctx.forceRerun, skipTierBump: true });
  }

  // ── Decisions inbox (provider/admin) ──────────────────────────────────────
  // Same roles as the pain surfaces; the enumeration itself lives in
  // decision-inbox.js so the inbox and the generate-time gates share one
  // source of truth. isBilled is consulted PER KIND: tier + pain-ack ride the
  // CCM bill; bhi-ack rides the BHI leg (bhi_bn slot in billedState).
  const decisionItems = useMemo(() => {
    if (!canManagePain || loading) return [];
    return pendingDecisions(patients, month, {
      staffList: painStaffList,
      rankTiers: rankCcmTiersByReimbursement,
      canManagePain,
      isBilled: (pid, kind) => {
        const slot = billedState && billedState[month] && billedState[month][pid];
        if (!slot) return false;
        return kind === 'bhi-ack' ? !!slot[BHI_BN_KEY] : true;
      },
    }).filter((it) => !(it.kind === 'tier' && inboxKeptStandard.has(`${it.patientId}:${month}`)));
  }, [canManagePain, loading, patients, month, painStaffList, billedState, inboxKeptStandard]);

  // Practices with no CCM-ladder/BHI/pain membership never see the section —
  // a zero-state is only meaningful where decisions can occur.
  const hasDecisionPrograms = useMemo(() => canManagePain && patients.some((p) => {
    const raw = p._raw || p || {};
    const b = raw.billing || {};
    return String(b.program || '') === '99490' || !!b.bhiProgram || isPainEnrolledPatient(raw);
  }), [canManagePain, patients]);

  // Tier decision from the inbox: persist via the SAME write path as the
  // generate-flow modal (writeCcmTierAffirmation) but do NOT generate — the
  // note run stays a separate act. "Keep 99490" clears any stored override
  // and resolves the row session-locally (nothing is persisted for "decided
  // to keep standard"; the generate-time review will confirm it).
  async function applyInboxTierAffirm(affirmByPatient) {
    const ctx = inboxTierReview;
    if (!ctx || inboxTierBusy) return;
    const affirm = affirmByPatient && affirmByPatient[ctx.patient.id];
    if (!affirm) { setInboxTierReview(null); return; }
    setInboxTierBusy(true);
    const row = ctx.rows[0] || {};
    try {
      // ctx.mStr snapshots the month the item was opened against — the write
      // must never follow a mid-dialog month switch.
      const updated = await writeCcmTierAffirmation(ctx.patient, ctx.mStr, affirm, {
        affirmedBy: (currentUser && (currentUser.name || currentUser.email)) || 'operator',
        minutes: row.minutes, physicianPct: row.physicianPct,
      });
      setPatients((prev) => prev.map((p) => (p.id === ctx.patient.id ? { ...p, _raw: updated._raw || p._raw } : p)));
      const kept = !affirm.mdm && !affirm.physicianTime;
      if (kept) setInboxKeptStandard((prev) => new Set(prev).add(`${ctx.patient.id}:${ctx.mStr}`));
      setToast({
        id: 'inbox-tier-' + Date.now(),
        text: kept
          ? `${ctx.patient.name}: keeping 99490 for ${ctx.mStr} (confirmed again at generate).`
          : `${ctx.patient.name}: ${affirm.mdm ? '99487 (Complex)' : '99491 (Practitioner-Led)'} affirmed for ${ctx.mStr} ✓`,
        undo: null,
      });
      setInboxTierReview(null);
    } catch (e) {
      setToast({ id: 'inbox-tier-err-' + Date.now(), text: 'Tier affirmation failed: ' + (e?.message || 'unknown'), undo: null, error: true });
    } finally {
      setInboxTierBusy(false);
    }
  }

  // Ack items route through handleGenerate so EVERY pre-generate gate runs —
  // the relevant review modal (BHI no-assessment / pain no-scale) surfaces as
  // part of the normal chain; the inbox never bypasses or duplicates it.
  function openDecisionAction(item) {
    const p = patients.find((x) => x.id === item.patientId);
    if (!p) return;
    if (item.kind === 'tier') {
      setInboxTierReview({
        rows: [{ patientId: p.id, name: p.name, minutes: item.minutes, physicianPct: item.physicianPct, existingCode: null }],
        patient: p, mStr: month,
      });
      return;
    }
    handleGenerate(p, month, item.kind === 'bhi-ack' ? { program: 'bhi' } : {});
  }

  async function handlePushAndSign(p, mStr, opts) {
    const key = `${p.id}:${mStr}`;
    if (pushSignPatients[key]) return;
    const ws = getWorkflowState(p, mStr, billedState, billingNotesMap, opts || {});
    if (!ws.hasBillingNote) {
      setToast({ id: 'no-gen-' + Date.now(), text: `Generate ${p.name}'s draft first.`, undo: null, error: true });
      return;
    }
    if (ws.isBilled) {
      setToast({ id: 'already-' + Date.now(), text: `${p.name} already submitted this cycle. Regenerate to re-push.`, undo: null, error: true });
      return;
    }
    // BHI SOFT-guard on the push leg: if this BHI bill has no initial assessment
    // on file, require an explicit warned confirmation before shipping it to
    // Elation. The push route itself is permissive, so this is the audited
    // acknowledgement point. `_assessmentAcknowledged` short-circuits the modal
    // when we re-enter from its Proceed handler.
    if ((opts || {}).program === 'bhi'
        && !(opts || {})._assessmentAcknowledged
        && bhiNeedsAssessmentOverride(p, mStr)) {
      setBhiNoAssessmentReview({ patient: p, mStr, mode: 'push' });
      return;
    }
    // CPM is a concurrent component of the CCM/APCM combined bill. The push
    // leg revalidates the stored note's G3002 and needs the same explicit
    // acknowledgement when the monthly scale is the only failing gate.
    if ((opts || {}).program !== 'bhi'
        && !(opts || {})._painScaleAcknowledged
        && painNeedsScaleAck(p, mStr)) {
      setPainNoScaleReview({ patient: p, mStr, mode: 'push' });
      return;
    }
    if (getPref('confirmBeforePush')) {
      const ok = await confirmDialog({
        title: `Push & sign for ${p.name}?`,
        body: `Push ${p.name}'s draft to Elation and sign it.`,
        confirmLabel: 'Push & sign',
      });
      if (!ok) return;
    }

    setPushSignPatients((prev) => ({ ...prev, [key]: true }));
    const tid = 'ps-' + Date.now();
    setToast({ id: tid, text: `Pushing ${p.name} to Elation…`, undo: null });
    // Reconcile a reported failure against the server's billingPush slot before
    // declaring failure — the bill may have actually landed (a lost response or
    // a post-push billed-state blip surfaces as ok:false / a throw even though
    // Elation has the note). Avoids the "says failed but really pushed" toast.
    const reportPushFailure = async (rawMsg) => {
      const landed = await checkBillLanded(p.id, mStr, (opts || {}).program);
      if (landed === 'pushed') {
        setToast({ id: tid, text: `${p.name} was already pushed to Elation — verify/sign there ✓`, undo: null });
        setTimeout(() => setToast(t => (t && t.id === tid ? null : t)), 4500);
        setDraftRefreshKey(k => k + 1);
        return;
      }
      if (landed === 'bill_failed') {
        setToast({ id: tid, text: `${p.name}: note filed in Elation but the bill failed — resolve it in Elation.`, undo: null, error: true });
        setTimeout(() => setToast(t => (t && t.id === tid ? null : t)), 8000);
        return;
      }
      setToast({ id: tid, text: `Push & Sign failed: ${rawMsg}`, undo: null, error: true });
      setTimeout(() => setToast(t => (t && t.id === tid ? null : t)), 6000);
    };
    try {
      const res = await pushAndSignForPatient(p, mStr, {
        program: (opts || {}).program || undefined,
        assessmentOverride: (opts || {})._assessmentAcknowledged === true,
        _painScaleAcknowledged: (opts || {})._painScaleAcknowledged === true,
        onStep: ({ step }) => {
          const labels = {
            'billing-push': `${p.name}: pushing billing…`,
            'phone-push':   `${p.name}: pushing phone note…`,
            'sign':         `${p.name}: signing in Elation…`,
            'billed-state': `${p.name}: finalizing…`,
          };
          if (labels[step]) setToast({ id: tid, text: labels[step], undo: null });
        },
      });
      if (res.skipped) {
        setToast({ id: tid, text: `${p.name} skipped — ${res.reason}`, undo: null, error: true });
        setTimeout(() => setToast(t => (t && t.id === tid ? null : t)), 4500);
      } else if (res.ok) {
        const signedNote = res.results && res.results.signed ? 'pushed & signed' : 'pushed (unsigned — sign in Elation UI)';
        setToast({ id: tid, text: `${p.name} ${signedNote} ✓`, undo: null });
        setTimeout(() => setToast(t => (t && t.id === tid ? null : t)), 3500);
        setDraftRefreshKey(k => k + 1);
      } else {
        await reportPushFailure(res.errors.join('; ') || 'push failed');
      }
    } catch (err) {
      await reportPushFailure(err?.message || 'push failed');
    } finally {
      setPushSignPatients((prev) => { const n = { ...prev }; delete n[key]; return n; });
    }
  }

  // Handler invoked by BhiNoAssessmentReviewModal once the operator explicitly
  // confirms proceeding without an initial assessment. Threads
  // { assessmentOverride: true } into the generate path (or re-enters the push
  // path with the acknowledgement flag). The override is audited downstream
  // (billing-generate.js / push-emr.js) and the note attestation is softened.
  async function handleBhiNoAssessmentProceed() {
    const ctx = bhiNoAssessmentReview;
    if (!ctx || bhiNoAssessmentBusy) return;
    const { patient: p, mStr, mode, forceRerun } = ctx;
    setBhiNoAssessmentBusy(true);
    try {
      setBhiNoAssessmentReview(null);
      if (mode === 'push') {
        await handlePushAndSign(p, mStr, { program: 'bhi', _assessmentAcknowledged: true });
      } else {
        await _runGenerate(p, mStr, null, { forceRerun: !!forceRerun, program: 'bhi', assessmentOverride: true });
      }
    } finally {
      setBhiNoAssessmentBusy(false);
    }
  }

  async function handlePainNoScaleProceed() {
    const ctx = painNoScaleReview;
    if (!ctx || painNoScaleBusy) return;
    const { patient: p, mStr, mode, forceRerun, freshness } = ctx;
    setPainNoScaleBusy(true);
    try {
      // Re-check against FRESH data before consuming the acknowledgement: the
      // modal's snapshot may predate a concurrent edit (consent revoked,
      // minutes removed, or a scale recorded) that changes what the override
      // would authorize. Fetch failure falls through to the snapshot — the
      // generate/push pipelines still run their own gates.
      try {
        const freshRaw = await ccmApi.getPatient(p.id);
        if (freshRaw && !painNeedsScaleAck({ ...p, _raw: freshRaw }, mStr)) {
          setPainNoScaleReview(null);
          setToast({
            id: 'pain-ack-stale-' + Date.now(),
            text: `${p.name || 'Patient'} changed since this dialog opened — re-run the action.`,
            undo: null,
            error: true,
          });
          return;
        }
      } catch (_) { /* stale-snapshot fallback */ }
      await safeAudit('PAIN_NO_SCALE_ACK', p.id,
        'billing G3002 for ' + mStr + ' acknowledged with no validated pain scale on file'
      ).catch(() => {});
      setPainNoScaleReview(null);
      if (mode === 'push') {
        await handlePushAndSign(p, mStr, { _painScaleAcknowledged: true });
      } else {
        await _runGenerate(p, mStr, freshness || null, { forceRerun: !!forceRerun, painScaleOverride: true });
      }
    } finally {
      setPainNoScaleBusy(false);
    }
  }

  // Handler invoked by ComplexCcmReviewModal once the operator picks proceed
  // or downgrade. Now fires Generate (not Push) — the CMS revision decision
  // belongs at plan-write time.
  async function handleComplexCcmDecision({ action, newProgram }) {
    if (!complexCcmReview || complexCcmBusy) return;
    const { patient: p, monthStr: mStr, eligibility, forceRerun, program } = complexCcmReview;
    setComplexCcmBusy(true);
    try {
      let patientToPush = p;
      if (action === 'downgrade') {
        const prevProgram = (p._raw && p._raw.billing && p._raw.billing.program) || '';
        const nextRaw = {
          ...p._raw,
          billing: { ...(p._raw && p._raw.billing ? p._raw.billing : {}), program: newProgram },
        };
        const resp = await ccmApi.updatePatient(p.id, nextRaw);
        const stamped = {
          ...nextRaw,
          _updatedAt: (resp && resp.updatedAt) || nextRaw._updatedAt,
        };
        patientToPush = { ...p, _raw: stamped, program: newProgram };
        setPatients(prev => prev.map(x => {
          if (x.id !== p.id) return x;
          const re = adaptPatient({ id: x.id, data: stamped }, month);
          return re || { ...x, _raw: stamped, program: newProgram };
        }));
        try {
          await ccmApi.auditEvent(
            'COMPLEX_CCM_REVIEW_DOWNGRADE',
            'patients',
            p.id,
            `Complex CCM review for ${mStr}: downgraded ${prevProgram || '99487'} → ${newProgram} (no new labs/notes since ${eligibility.sinceDate || 'prior plan'})`,
          );
        } catch (_) {}
      } else if (action === 'proceed') {
        try {
          await ccmApi.auditEvent(
            'COMPLEX_CCM_REVIEW_PROCEED',
            'patients',
            p.id,
            `Complex CCM review for ${mStr}: proceeded with 99487 despite no new labs/notes since ${eligibility.sinceDate || 'prior plan'}`,
          );
        } catch (_) {}
      }
      setComplexCcmReview(null);
      await runPreGenerateGates(patientToPush, mStr, { program, forceRerun, freshness: eligibility.freshness, skipTierBump: true, skip99487: true });
    } catch (err) {
      const errId = 'err' + Date.now();
      setToast({ id: errId, text: err?.message || 'Review apply failed', undo: null, error: true });
      setTimeout(() => setToast(t => (t && t.id === errId ? null : t)), 5000);
      setComplexCcmReview(null);
    } finally {
      setComplexCcmBusy(false);
    }
  }

  // Clinician action: mark a PHQ-9 item-9 at-risk assessment reviewed. The
  // server route does the transactional write (sets riskReviewedAt/By); we then
  // stamp riskReviewedAt locally and re-adapt the one patient (same idiom as the
  // complex-CCM downgrade above) so the chip + nav badge predicate clear without
  // a full list refetch.
  async function reviewRisk(p) {
    const tss = openRiskAssessmentTimestamps(p._raw || {});
    if (!tss.length || reviewingRisk[p.id]) return;
    setReviewingRisk((m) => ({ ...m, [p.id]: true }));
    try {
      for (const ts of tss) await ccmApi.reviewAssessment(p.id, ts); // usually exactly one
      const nowIso = new Date().toISOString();
      const nextRaw = {
        ...p._raw,
        bhi: {
          ...(p._raw && p._raw.bhi ? p._raw.bhi : {}),
          assessments: ((p._raw && p._raw.bhi && p._raw.bhi.assessments) || []).map((a) =>
            (a && a.riskFlag && !a.riskReviewedAt ? { ...a, riskReviewedAt: nowIso } : a)),
        },
      };
      setPatients(prev => prev.map(x => {
        if (x.id !== p.id) return x;
        const re = adaptPatient({ id: x.id, data: nextRaw }, month);
        return re || { ...x, _raw: nextRaw };
      }));
      const tid = 'riskrev' + Date.now();
      setToast({ id: tid, text: `${p.name}: at-risk assessment marked reviewed.`, undo: null });
      setTimeout(() => setToast(t => (t && t.id === tid ? null : t)), 4000);
    } catch (err) {
      const errId = 'riskreverr' + Date.now();
      setToast({ id: errId, text: err?.message || 'Could not mark reviewed — try again.', undo: null, error: true });
      setTimeout(() => setToast(t => (t && t.id === errId ? null : t)), 5000);
    } finally {
      setReviewingRisk((m) => { const n = { ...m }; delete n[p.id]; return n; });
    }
  }

  // Per-lens row hints (BHI / All lenses only). Used by both the full-row and
  // compact-row renderers so dual-enrollment / G0570-fold / no-initial-assessment
  // states are surfaced regardless of which section grouping is active.
  function rowHints(p) {
    const pRaw = p._raw || {};
    const pBhiEnrolled = isBhiEnrolledPatient(pRaw);
    const pCcmEnrolled = isEnrolledProgram(pRaw.billing?.program);
    const pBhiCode = pBhiEnrolled ? resolveBhiBillCode(pRaw, month) : null;
    const pLatestAssess = (pRaw.bhi?.assessments || []).slice(-1)[0] || null;
    const pHasOpenRisk = hasOpenAssessmentRisk(pRaw);

    let bhiHint = null;
    if (pBhiEnrolled) {
      if (pBhiCode === 'G0570') {
        bhiHint = <span className="row-hint row-hint-fold">→ G0570</span>;
      } else if (!pLatestAssess) {
        bhiHint = <span className="row-hint row-hint-warn">⚠ no initial</span>;
      } else {
        bhiHint = (
          <span className="row-hint row-hint-assess">
            {pLatestAssess.scale}: {pLatestAssess.score}
          </span>
        );
      }
    }

    return (
      <>
        {pHasOpenRisk && (
          <button
            type="button"
            className="row-hint row-hint-risk"
            disabled={!!reviewingRisk[p.id]}
            title="At-risk assessment (PHQ-9 item 9) — click to mark reviewed"
            onClick={(e) => { e.stopPropagation(); reviewRisk(p); }}
          >
            {reviewingRisk[p.id] ? '⚠ reviewing…' : '⚠ at-risk · mark reviewed'}
          </button>
        )}
        {programFilter === 'bhi' && bhiHint}
        {programFilter === 'all' && (pCcmEnrolled || pBhiEnrolled) && (
          <span className="row-program-pills">
            {pCcmEnrolled && <span className="program-pill program-pill-ccm">CCM</span>}
            {pBhiEnrolled && <span className="program-pill program-pill-bhi">BHI</span>}
          </span>
        )}
      </>
    );
  }

  function renderFullRow(p) {
    const liveLogged = getLogged(p);
    const liveStatus = getLiveStatus(p);
    const handleKeySelect = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        selectPatient(p.id);
      }
    };

    return (
      <div key={p.id}
           className={`roster-item ${p.id === selectedId ? 'active' : ''}`}
           role="button"
           tabIndex={0}
           aria-pressed={p.id === selectedId}
           aria-label={`Select ${p.name}`}
           onClick={() => selectPatient(p.id)}
           onKeyDown={handleKeySelect}>
        <div className={`avatar av-${p.avSlot}`}>{init(p.name)}</div>
        <div>
          <div className="ri-name">
            {p.name}
            {rowHints(p)}
          </div>
          <div className="ri-meta">
            {(p.age != null || p.sex) && <span>{p.age != null ? p.age : ''}{p.sex}</span>}
            {p.dx.length > 0 && <><span className="sep">·</span><span>{p.dx.slice(0, 2).join(', ')}</span></>}
            {p.alerts > 0 && <><span className="sep">·</span><span style={{ color: 'var(--warn)' }}>{p.alerts} alert{p.alerts > 1 ? 's' : ''}</span></>}
          </div>
          <ProgressTinybar value={liveLogged} target={p.target} />
        </div>
        <div className="ri-right">
          {p.apcmDecision ? (
            <div className="apcm-tier-chip" title={p.apcmDecision.reason}>{p.apcmDecision.code}</div>
          ) : (
            <div className="ri-time tabnum">{p.target ? `${liveLogged}/${p.target}m` : 'APCM'}</div>
          )}
          {p.target && !p.apcmDecision ? <div className="ri-status"><StatusPill status={liveStatus} sm /></div> : null}
        </div>
      </div>
    );
  }

  function renderCompactRow(p) {
    const liveLogged = getLogged(p);
    const handleKeySelect = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        selectPatient(p.id);
      }
    };
    return (
      <div key={p.id}
           className={`roster-item compact ${p.id === selectedId ? 'active' : ''}`}
           role="button"
           tabIndex={0}
           aria-pressed={p.id === selectedId}
           aria-label={`Select ${p.name}`}
           onClick={() => selectPatient(p.id)}
           onKeyDown={handleKeySelect}>
        <div className={`avatar sm av-${p.avSlot}`}>{init(p.name)}</div>
        <div className="rc-name">
          {p.name}
          {rowHints(p)}
        </div>
        <div className="rc-time tabnum">{p.target ? `${liveLogged}/${p.target}m` : 'APCM'}</div>
        <span className="rc-check" aria-hidden="true">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M4 12l5 5L20 6"/></svg>
        </span>
      </div>
    );
  }

  function Section({ id, title, dotClass, rows, compact }) {
    if (rows.length === 0) return null;
    return (
      <div className="roster-section" ref={el => { sectionRefs.current[id] = el; }}>
        <div className="roster-section-head">
          <span className={`rs-dot ${dotClass}`} aria-hidden="true"/>
          <span className="rs-title">{title}</span>
          <span className="rs-count tabnum">{rows.length}</span>
        </div>
        {rows.map(p => (compact ? renderCompactRow(p) : renderFullRow(p)))}
      </div>
    );
  }

  // Keyboard navigation (disabled on narrow screens where there's no keyboard).
  // Pref read inside the handler so toggling the setting takes effect immediately.
  useEffect(() => {
    if (isNarrow) return;
    const handler = (e) => {
      if (!getPref('keyboardShortcuts')) return;
      if (settingsFor || showPush) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const idx = filtered.findIndex(p => p.id === selectedId);
      if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); if (!selectedId && filtered[0]) setSelectedId(filtered[0].id); else if (idx < filtered.length - 1) setSelectedId(filtered[idx + 1].id); }
      if (e.key === 'k' || e.key === 'ArrowUp')   { e.preventDefault(); if (idx > 0) setSelectedId(filtered[idx - 1].id); }
      if (e.key === 'Escape' && selectedId)       { e.preventDefault(); setSelectedId(null); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [filtered, selectedId, settingsFor, showPush, isNarrow]);

  // Counts for the filter pills + push button.
  const liveCounts = useMemo(() => {
    let ready = 0, short = 0;
    patients.forEach(p => {
      if (!p.target) return;
      const l = getLogged(p);
      if (l >= p.target) ready++;
      else short++;
    });
    return { all: patients.length, ready, short };
  }, [patients]);

  // Pushable count for the chrome-bar Push button. Uses the same eligibility
  // function the modal uses so the count matches the modal's "Ready to push"
  // figure (consent + program + Elation link + not-already-submitted + time
  // threshold per program).
  const pushableCount = useMemo(() => {
    let n = 0;
    for (const p of patients) {
      const elig = getPushEligibility(p, month, billedState);
      if (elig.eligible) n++;
    }
    return n;
  }, [patients, month, billedState]);

  // Chrome-bar workflow state — drives the label on the single chrome-bar
  // button. Generate takes priority: if anyone is eligible-not-yet-generated,
  // the bar says "Generate N · MMMM"; once that bucket is empty we flip to
  // "Push & Sign N · MMMM" for the generated-but-not-billed bucket.
  //
  // Program-aware (Phase 7): counts unique patients with >=1 program in the
  // stage. APCM-folded BHI (resolveBhiBillCode === 'G0570') is not counted on
  // the BHI side because the CCM combined note already carries G0570.
  const chromeWorkflow = useMemo(() => {
    const programs = programFilter === 'all' ? ['ccm', 'bhi']
                   : programFilter === 'bhi' ? ['bhi']
                   : ['ccm'];
    let generateN = 0;
    let pushSignN = 0;
    for (const p of patients) {
      let needsGen = false;
      let needsPush = false;
      for (const program of programs) {
        if (program === 'bhi' && !isBhiEnrolledPatient(p._raw)) continue;
        if (program === 'ccm' && !isEnrolledProgram(p._raw?.billing?.program)) continue;
        if (program === 'bhi' && resolveBhiBillCode(p._raw, month) === 'G0570') continue;
        const elig = getPushEligibility(p, month, billedState, { program, ignoreAlreadySubmitted: true });
        // BHI branch of getPushEligibility returns `ready`, CCM returns `eligible`.
        // Match push-modal.jsx's dual-shape check.
        if (!elig.eligible && !elig.ready) continue;
        const ws = getWorkflowState(p, month, billedState, billingNotesMap, { program });
        if (ws.needsGenerate) needsGen = true;
        else if (ws.needsPushSign) needsPush = true;
      }
      if (needsGen) generateN++;
      else if (needsPush) pushSignN++;
    }
    const stage = generateN > 0 ? 'generate' : (pushSignN > 0 ? 'pushsign' : 'idle');
    return { generateN, pushSignN, stage };
  }, [patients, month, billedState, billingNotesMap, programFilter]);

  const pushTasks = useMemo(
    () => tasksForRoster(patients, month, programFilter),
    [patients, month, programFilter]
  );

  // Unified program-card view-models for the StatusRail. One descriptor per
  // visible program (CCM and/or BHI), each carrying its workflow/eligibility/
  // gate state plus the wired Generate / Push & Sign / redirect / history
  // handlers. Computed here at the top level (guarded by `if (!patient)`) so it
  // sits with the other render-time hooks and never trips Rules of Hooks.
  const railCards = useMemo(() => {
    if (!patient) return [];
    const out = [];
    const ccmVisible = isEnrolledProgram(patient._raw?.billing?.program)
      && (programFilter === 'ccm' || programFilter === 'all' || !programFilter);
    const bhiEnrolled = isBhiEnrolledPatient(patient._raw);
    const bhiVisible = bhiEnrolled && (programFilter === 'bhi' || programFilter === 'all' || !programFilter);

    if (ccmVisible) {
      const elig = getPushEligibility(patient, month, billedState, { ignoreAlreadySubmitted: true });
      const ws = getWorkflowState(patient, month, billedState, billingNotesMap);
      const apcmDecision = getApcmDecisionForMonth(patient);
      const model = programCardModel({
        program: 'ccm',
        code: apcmDecision ? `APCM ${apcmDecision.code}` : (patient.programLabel || patient.program),
        label: ccmSlotLabel(patient, month),
        isApcm: !!apcmDecision || !patient.target,
        target: patient.target, logged: getLogged(patient),
        eligible: !!elig.eligible, eligReason: elig.reason,
        workflow: ws,
        busy: {
          gen: !!generatingPatients[`${patient.id}:${month}`],
          push: !!pushSignPatients[`${patient.id}:${month}`],
        },
        genError: genErrors[`${patient.id}:${month}`] || null,
      });
      out.push({
        model,
        onGenerate: () => handleGenerate(patient, month),
        onPush: () => handlePushAndSign(patient, month),
      });
    }

    if (bhiVisible) {
      const bhiCode = resolveBhiBillCode(patient._raw, month);
      // The missing-assessment gate is a SOFT guard for the standalone 99484
      // card: compute the card's gates + eligibility WITH the override so the
      // card stays actionable (variant 'bhi-normal', a '⚠ No initial
      // assessment' warning, buttons enabled). The click is still held behind
      // an explicit confirmation in handleGenerate/handlePushAndSign, which
      // threads the real override + audits it. The override is NOT applied to
      // the folded G0570 card (that add-on stays hard-gated on the APCM note).
      const bhiSoftOverride = bhiCode !== 'G0570';
      const bhiGates = bhiGatesForCode(patient._raw, month, bhiCode, { assessmentOverride: bhiSoftOverride });
      const bhiElig = getPushEligibility(patient, month, billedState, { ignoreAlreadySubmitted: true, program: 'bhi', assessmentOverride: bhiSoftOverride });
      const bhiWs = getWorkflowState(patient, month, billedState, billingNotesMap, { program: 'bhi' });
      // Month-capped, same helper the gate uses — so an assessment that
      // postdates the service month shows "No initial assessment" rather than a
      // "Latest: PHQ-9…" that the gate itself doesn't honor for this month.
      const latest = _latestAssessmentForMonth(patient._raw?.bhi?.assessments, month) || null;
      const model = programCardModel({
        program: 'bhi',
        code: bhiCode === 'G0570' ? 'BHI→G0570' : `BHI ${bhiCode}`,
        label: bhiCode === 'G0570' ? 'BHI → G0570' : `BHI (${bhiCode})`,
        folded: bhiCode === 'G0570',
        gates: bhiGates,
        target: 20, logged: bhiMonthlyMinutes(patient, month),
        eligible: !!(bhiElig.eligible || bhiElig.ready), eligReason: bhiElig.reason,
        workflow: bhiWs,
        assessment: latest ? { scale: latest.scale, score: latest.score, severity: latest.severity, date: latest.date } : null,
        busy: {
          gen: !!generatingPatients[`${patient.id}:${month}`],
          push: !!pushSignPatients[`${patient.id}:${month}`],
        },
        genError: genErrors[`${patient.id}:${month}`] || null,
      });
      out.push({
        model,
        onGenerate: () => handleGenerate(patient, month, { program: 'bhi' }),
        onPush: () => handlePushAndSign(patient, month, { program: 'bhi' }),
        onRedirect: (target) => {
          if (target === 'ccm') setProgramFilter && setProgramFilter('ccm');
          else if (target === 'timelogs') onJumpToTimePatient && onJumpToTimePatient(patient.id);
        },
        onHistory: () => setHistoryOpen(true),
      });
    }
    return out;
  }, [patient, month, billedState, billingNotesMap, programFilter, generatingPatients, pushSignPatients]);

  const canFocusSection = FOCUSABLE_DOCUMENT_TABS.has(tab);
  const focusActive = documentFocus && canFocusSection && !!selectedId;
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (focusActive) document.body.setAttribute('data-document-focus', 'true');
    else document.body.removeAttribute('data-document-focus');
    return () => document.body.removeAttribute('data-document-focus');
  }, [focusActive]);

  // Local component — shown on the BHI sub-tab when the patient is APCM-decided.
  // BHI add-on for APCM patients bills as G0570 attached to the combined note,
  // not as a standalone 99484 visit note — so no bhi_bn row exists to display.
  // Gate-aware: when consent + assessment pass, G0570 will attach; when they
  // fail, lib silently drops G0570 from the combined codes and the patient
  // bills APCM only. Mirror the Studio BHI slot's pass/warn split (Task 7).
  function FoldedIntoApcmExplainer({ onSwitchToCcm, gatesOk, blockers }) {
    if (gatesOk) {
      return (
        <div className="doc-section">
          <div className="billing-folded-explainer">
            <div style={{ fontWeight: 500, marginBottom: 6 }}>BHI is folded into the APCM bill (G0570)</div>
            <div style={{ marginBottom: 12 }}>
              This patient's BHI add-on bills as G0570 attached to the APCM combined note,
              not as a standalone 99484 visit note. Switch to the APCM sub-tab to view the
              combined billing note.
            </div>
            <button className="btn sm accent" onClick={onSwitchToCcm}>
              View APCM note →
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="doc-section">
        <div className="billing-folded-explainer">
          <div style={{ fontWeight: 500, marginBottom: 6, color: 'var(--warn, #c47b1d)' }}>
            ⚠ BHI add-on (G0570) not ready
          </div>
          <ul style={{ margin: '0 0 8px 16px', padding: 0 }}>
            {(blockers || []).map((b, i) => <li key={i}>{b}</li>)}
          </ul>
          <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--ink-3)' }}>
            G0570 will not attach to the APCM bill until these clear. Patient will bill APCM only.
          </div>
          <button className="btn sm accent" onClick={onSwitchToCcm}>
            View APCM note →
          </button>
          <button className="btn sm" style={{ marginLeft: 8 }} onClick={() => onJumpToTimePatient && onJumpToTimePatient(patient.id)}>
            Open in Time Logs (BHI view) →
          </button>
        </div>
      </div>
    );
  }

  // Local component — defined inside StudioPage so it closes over helpers
  // (getLogged, bhiMonthlyMinutes, monthLabel, etc.) without prop-drilling.
  // Rules of Hooks: this is a function definition, not a hook call, so it is
  // safe to define inside the render function body (before the return).
  function BillingSliceView({ slice, patient: p, month: m, monthLabel: mLabel }) {
    const isBhi = slice.key === 'bhi';
    const cpt = isBhi ? '99484' : (() => {
      const d = p._raw?.apcmMonth?.[m];
      return d?.code ? `APCM ${d.code}` : (p.programLabel || p.program);
    })();
    const programTone = isBhi ? 'bhi' : (String(cpt).startsWith('APCM') ? 'apcm' : 'ccm');
    const totalMin = isBhi ? bhiMonthlyMinutes(p, m) : getLogged(p);
    const target = isBhi ? 20 : (p.target || null);
    const summary = slice.draft.billing.summary;
    const noteTypes = slice.draft.billing.noteTypes || [];
    const isEditing = manualEdit?.section === 'billing' && manualEdit.program === slice.key;
    const saveDisabled = manualSaving
      || !manualEditText.trim()
      || manualEditText.trim() === String(manualEdit?.sourceText || '').trim();

    return (
      <>
        <div className={`doc-section billing-program-section program-${programTone}`}>
          <h3>{mLabel || m} summary</h3>
          <div className={`bill-summary program-${programTone}`}>
            <div className="bill-stat">
              <div className="label">CPT</div>
              <div className="value" style={{ fontSize: 14, marginTop: 8 }}>{cpt}</div>
            </div>
            <div className="bill-stat">
              <div className="label">Time logged</div>
              <div className="value tabnum">
                {totalMin}<span style={{ fontSize: 13, color: 'var(--ink-3)', marginLeft: 3 }}>min</span>
              </div>
              <div className="sub">Target: {target != null ? `${target}m` : 'flat'}</div>
            </div>
            <div className="bill-stat">
              <div className="label">Activities</div>
              <div className="value tabnum">{slice.draft.billing.activities || 0}</div>
              <div className="sub">Phone, portal, coord</div>
            </div>
            <div className="bill-stat">
              <div className="label">Est. revenue</div>
              <div className="value tabnum">${slice.draft.billing.revenue || 0}</div>
              <div className="sub">Pending push</div>
            </div>
          </div>
        </div>
        <div className="doc-section">
          <h3 className="doc-section-heading">
            <span className="doc-section-title">Billing note <span className="count">· auto-generated, editable</span></span>
            {canEditDocuments && (
              <button
                type="button"
                className="doc-edit-btn"
                onClick={() => isEditing
                  ? cancelManualEdit()
                  : startManualEdit('billing', { program: slice.key, text: summary || '', noteTypes })}
                aria-pressed={isEditing}>
                {isEditing ? 'Editing' : summary ? 'Edit' : 'Write manually'}
              </button>
            )}
          </h3>
          {isEditing ? (
            <ManualDocumentEditor
              label="Billing note"
              value={manualEditText}
              onChange={setManualEditText}
              onSave={saveManualDocument}
              onCancel={cancelManualEdit}
              saving={manualSaving}
              saveDisabled={saveDisabled}
            />
          ) : summary ? (
            <BillingNoteDocument text={summary} />
          ) : (
            <div style={{ padding: '20px 14px', color: 'var(--ink-3)', fontSize: 13, background: 'var(--surface-2)', borderRadius: 'var(--r)' }}>
              No billing note drafted for {mLabel || m} yet. Click <b>Generate</b> in the {isBhi ? 'BHI' : 'CCM'} slot above.
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <div className={`studio ${!selectedId ? 'collapsed' : ''} ${isNarrow ? `view-${mobileView}` : ''} ${focusActive ? 'document-focus' : ''} ${selectedId && !isNarrow ? 'detail-open' : ''}`}>
      {/* ── ROSTER ── */}
      <div className="studio-roster">
        <div className="roster-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--ink-3)' }}>
            <circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>
          </svg>
          <input placeholder="Search name or MRN…" value={query} onChange={e => setQuery(e.target.value)} />
          <span className="kbd">/</span>
        </div>
        <div className="roster-filters">
          {[
            { v: 'all',   l: 'All' },
            { v: 'ready', l: 'Ready' },
            { v: 'short', l: 'Short' },
          ].map(f => (
            <button key={f.v} className={`filter-pill ${filter === f.v ? 'active' : ''}`} onClick={() => setFilter(f.v)}>
              {f.l} <span className="n">{liveCounts[f.v]}</span>
            </button>
          ))}
        </div>
        <div className="mobile-workflow-summary studio-mobile-summary" aria-label="Review queue summary">
          <div><span>Short</span><b className="tabnum">{groups.short.length}</b></div>
          <div><span>APCM</span><b className="tabnum">{groups.apcm.length}</b></div>
          <div><span>Ready</span><b className="tabnum">{groups.ready.length}</b></div>
        </div>

        {/* Decisions inbox — pending operator decisions for the month. None of
            the v1 kinds hard-blocks billing (tier bump is optional revenue;
            the BHI/pain gates are acknowledge-able soft guards) and the copy
            must never overstate that — see decision-inbox.js. */}
        {hasDecisionPrograms && !loading && (
          <div className="decision-inbox" aria-label="Pending decisions">
            <div className="decision-inbox-head">
              <span className="decision-inbox-title">Decisions</span>
              {decisionItems.length > 0
                ? <span className="count tabnum">· {decisionCounts(decisionItems).total}</span>
                : <span className="decision-inbox-clear">none pending ✓</span>}
            </div>
            {decisionItems.map((it) => (
              <div key={`${it.kind}:${it.patientId}`} className="decision-inbox-item">
                <div className="decision-inbox-text">
                  <b>{it.name}</b>
                  {it.kind === 'tier' && (
                    <span className="decision-inbox-sub">
                      {it.minutes}′ CCM logged — tier affirmation open
                      {it.dollarDelta != null && it.dollarDelta > 0 && (
                        <> · <b className="tabnum">+${it.dollarDelta.toFixed(2)}</b> at {it.topCode} vs standard</>
                      )}
                      {it.physicianPct >= 0.5 && <> · {Math.round(it.physicianPct * 100)}% provider time</>}
                    </span>
                  )}
                  {it.kind === 'bhi-ack' && (
                    <span className="decision-inbox-sub">
                      Initial BHI assessment missing — record one, or acknowledge to bill 99484 without it
                    </span>
                  )}
                  {it.kind === 'pain-ack' && (
                    <span className="decision-inbox-sub">
                      No validated pain scale (NRS) this month — record one, or acknowledge to include G3002
                    </span>
                  )}
                </div>
                <div className="decision-inbox-actions">
                  {it.kind !== 'tier' && (
                    <button className="btn sm" onClick={() => onJumpToTimePatient && onJumpToTimePatient(it.patientId)}>
                      {it.kind === 'bhi-ack' ? 'Record assessment' : 'Record NRS'}
                    </button>
                  )}
                  <button className="btn sm primary" onClick={() => openDecisionAction(it)}>
                    {it.kind === 'tier' ? 'Review tier…' : 'Acknowledge…'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Progress rail — segmented bar with click-to-jump + scroll thumb */}
        {filter === 'all' && filtered.length > 8 && (
          <div className="roster-rail" aria-hidden="true">
            {(() => {
              const total = groups.short.length + groups.ready.length + groups.apcm.length;
              if (total === 0) return null;
              const pct = (n) => `${(n / total) * 100}%`;
              return (
                <>
                  {groups.short.length > 0 && <button className="rail-seg warn"   style={{ height: pct(groups.short.length) }} onClick={() => jumpToSection('short')} title={`${groups.short.length} short`}/>}
                  {groups.apcm.length  > 0 && <button className="rail-seg accent" style={{ height: pct(groups.apcm.length)  }} onClick={() => jumpToSection('apcm')}  title={`${groups.apcm.length} APCM fallback`}/>}
                  {groups.ready.length > 0 && <button className="rail-seg ok"     style={{ height: pct(groups.ready.length) }} onClick={() => jumpToSection('ready')} title={`${groups.ready.length} ready`}/>}
                  <div className="rail-thumb" style={{ top: `${scrollPct * 100}%` }}/>
                </>
              );
            })()}
          </div>
        )}

        <div className={`roster-list ${filter === 'all' && filtered.length > 8 ? 'has-rail' : ''}`} ref={rosterListRef}>
          {/* Resume from last patient */}
          {resumePatient && (
            <div className="resume-banner" onClick={() => { selectPatient(resumePatient.id); setResumeDismissed(true); }}>
              <span className="resume-icon" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>
              </span>
              <span className="resume-text">Resume from <b>{resumePatient.name}</b></span>
              <button className="resume-dismiss" aria-label="Dismiss"
                      onClick={(e) => { e.stopPropagation(); setResumeDismissed(true); }}>✕</button>
            </div>
          )}

          {loading && Array.from({ length: 6 }).map((_, i) => (
            <div key={`sk${i}`} className="roster-item skeleton">
              <div className="sk-avatar" />
              <div style={{ flex: 1 }}><div className="sk-line w70" /><div className="sk-line w40" /></div>
            </div>
          ))}
          {filter === 'all' ? (
            <>
              <Section id="short" title="Short"   dotClass="warn"   rows={groups.short} />
              <Section id="apcm"  title="APCM fallback" dotClass="accent" rows={groups.apcm}  />
              <Section id="ready" title="Ready"   dotClass="ok"     rows={groups.ready} compact />
            </>
          ) : (
            filtered.map(p => renderFullRow(p))
          )}
          {!loading && patients.length === 0 && (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--ink-3)' }}>
              <p style={{ fontSize: 16, color: 'var(--ink-2)', marginBottom: 8 }}>No onboarded patients in Review &amp; Push yet.</p>
              <p style={{ fontSize: 13 }}>Use <b>Onboard</b> to enroll patients and document consent, then return here.</p>
            </div>
          )}
          {!loading && patients.length > 0 && filtered.length === 0 && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
              No patients match.
            </div>
          )}
        </div>

        <div className="roster-footer">
          <span>{filtered.length} of {patients.length} patients</span>
          <span className="kbd">↑↓</span>
        </div>
      </div>

      {/* ── PREVIEW ── */}
      {selectedId && patient && (
        <div className="preview"
             onTouchStart={(e) => { if (!isNarrow) return; const t = e.touches[0]; swipeStart.current = { x: t.clientX, y: t.clientY, t: Date.now() }; }}
             onTouchEnd={(e) => {
               if (!isNarrow || !swipeStart.current) return;
               const t = e.changedTouches[0];
               const dx = t.clientX - swipeStart.current.x;
               const dy = t.clientY - swipeStart.current.y;
               const dt = Date.now() - swipeStart.current.t;
               swipeStart.current = null;
               if (dt > 500 || Math.abs(dx) < 60 || Math.abs(dy) > Math.abs(dx)) return;
               const idx = filtered.findIndex(p => p.id === selectedId);
               if (dx < 0 && idx < filtered.length - 1) setSelectedId(filtered[idx + 1].id);
               else if (dx > 0 && idx > 0) setSelectedId(filtered[idx - 1].id);
             }}>
          <StatusRail
            patient={patient}
            dx={patient.dx || []}
            cards={railCards}
            mode={isNarrow ? 'band' : 'rail'}
            expanded={isNarrow ? bandExpanded : true}
            onToggleExpanded={() => setBandExpanded(v => !v)}
            onBack={() => { if (isNarrow) setMobileView('roster'); else setSelectedId(null); }}
            onOverrides={() => setSettingsFor(patient)}
            onViewEmr={() => {
              const eid = patient?._raw?.elation_id;
              if (eid) window.open(`https://app.elationemr.com/patient/${eid}/req-action/`, '_blank', 'noopener');
            }}
            onClose={() => setSelectedId(null)}
            onEligibilityRefreshed={patchPatientFromEligibility}
          />

          <div className="preview-doc">
          <div className="preview-tabs">
            <button className={`preview-tab tab-careplan ${tab === 'careplan' ? 'active' : ''}`} onClick={() => setTab('careplan')}>
              Care plan
            </button>
            <button className={`preview-tab tab-billing ${tab === 'billing' ? 'active' : ''}`} onClick={() => setTab('billing')}>
              Billing note
            </button>
            <button className={`preview-tab tab-timelog ${tab === 'timelog' ? 'active' : ''}`} onClick={() => setTab('timelog')}>
              Time log <span style={{ color: 'var(--ink-3)', fontSize: 11, marginLeft: 2 }}>{draft.timelog.length}</span>
            </button>
            {canFocusSection && (
              <button
                className={`document-focus-toggle ${focusActive ? 'active' : ''}`}
                onClick={() => setDocumentFocus(v => !v)}
                title={focusActive ? 'Exit reading view' : 'Open reading view'}
                aria-pressed={focusActive}>
                {focusActive ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M8 3v5H3"/><path d="M16 3v5h5"/><path d="M8 21v-5H3"/><path d="M16 21v-5h5"/>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 9V3h6"/><path d="M21 9V3h-6"/><path d="M3 15v6h6"/><path d="M21 15v6h-6"/>
                  </svg>
                )}
                {focusActive ? 'Exit reading' : 'Reading view'}
              </button>
            )}
          </div>

          <div className="preview-body">
            {tab === 'careplan' && (
              <>
                {(() => {
                  const isEditing = manualEdit?.section === 'careplan';
                  const saveDisabled = manualSaving
                    || !manualEditText.trim()
                    || manualEditText.trim() === String(manualEdit?.sourceText || '').trim();
                  const header = (
                    <h3 className="doc-section-heading">
                      <span className="doc-section-title">Care plan <span className="count">· {monthLabel || month}</span></span>
                      {canEditDocuments && (
                        <button
                          type="button"
                          className="doc-edit-btn"
                          onClick={() => isEditing
                            ? cancelManualEdit()
                            : startManualEdit('careplan', { text: draft.careplan.ai || '' })}
                          aria-pressed={isEditing}>
                          {isEditing ? 'Editing' : draft.careplan.ai ? 'Edit' : 'Write manually'}
                        </button>
                      )}
                    </h3>
                  );
                  if (isEditing) {
                    return (
                      <div className="doc-section">
                        {header}
                        <ManualDocumentEditor
                          label="Care plan"
                          value={manualEditText}
                          onChange={setManualEditText}
                          onSave={saveManualDocument}
                          onCancel={cancelManualEdit}
                          saving={manualSaving}
                          saveDisabled={saveDisabled}
                        />
                      </div>
                    );
                  }
                  if (!draft.careplan.ai) {
                    return (
                      <div className="doc-section">
                        {header}
                        <div style={{ padding: '20px 14px', color: 'var(--ink-3)', fontSize: 13, background: 'var(--surface-2)', borderRadius: 'var(--r)' }}>
                          No care plan drafted for {monthLabel || month} yet. Click <b>Generate &amp; Push to EMR</b> to generate, save, and push.
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="doc-section">
                      {header}
                      <CarePlanDocument text={draft.careplan.ai} />
                    </div>
                  );
                })()}
              </>
            )}

            {tab === 'billing' && (() => {
              const ccmEnrolled = isEnrolledProgram(patient._raw?.billing?.program);
              const bhiEnrolled = isBhiEnrolledPatient(patient._raw);

              const slices = [];
              const apcmDecided = !!(patient._raw?.apcmMonth?.[month]);
              if (ccmEnrolled) slices.push({ key: 'ccm', label: ccmSlotLabel(patient, month), draft: ccmDraft });
              if (bhiEnrolled) {
                const bhiSliceGates = apcmDecided ? resolveBhiBillCodeWithGates(patient._raw, month) : null;
                slices.push({
                  key: 'bhi',
                  label: 'BHI',
                  draft: bhiDraft,
                  foldedIntoApcm: apcmDecided,
                  bhiGates: bhiSliceGates,
                });
              }

              const activeSlice = slices.find(s => s.key === billingSub) || slices[0];
              if (!activeSlice) {
                return (
                  <div className="doc-section">
                    <div style={{ padding: 20, color: 'var(--ink-3)', fontSize: 13 }}>
                      Not enrolled in CCM or BHI for this month.
                    </div>
                  </div>
                );
              }

              return (
                <>
                  {slices.length > 1 && (
                    <div className="billing-subtabs" role="tablist" aria-label="Billing program">
                      {slices.map(s => (
                        <button
                          key={s.key}
                          role="tab"
                          aria-selected={billingSub === s.key}
                          className={`billing-subtab program-${s.key} ${billingSub === s.key ? 'active' : ''}`}
                          onClick={() => setBillingSub(s.key)}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {activeSlice.foldedIntoApcm ? (
                    <FoldedIntoApcmExplainer
                      onSwitchToCcm={() => setBillingSub('ccm')}
                      gatesOk={!activeSlice.bhiGates || activeSlice.bhiGates.gatesOk}
                      blockers={activeSlice.bhiGates ? activeSlice.bhiGates.blockers : []}
                    />
                  ) : (
                    <BillingSliceView slice={activeSlice} patient={patient} month={month} monthLabel={monthLabel} />
                  )}
                </>
              );
            })()}

            {tab === 'timelog' && (
              <div className="doc-section">
                <h3>Time entries <span className="count">· {draft.timelog.length} entries · {sumMinutes(draft.timelog)} min total</span></h3>
                {draft.timelog.length === 0 ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13, background: 'var(--surface-2)', borderRadius: 'var(--r)' }}>
                    No time logged this month.
                  </div>
                ) : (
                  <div className="timelog">
                    {draft.timelog.map((t, i) => (
                      <div className="timelog-row" key={t.id || i}>
                        <span className="date">{t.date}</span>
                        <div>
                          <div className="kind">{t.kind || 'manual'}</div>
                          <div className="desc">{t.desc || t.label || ''}</div>
                        </div>
                        <span className="src">{t.src === 'voice' ? '🎙 voice-AI' : '⌨ manual'}</span>
                        <span className="mins tabnum">{Number(t.mins) || Number(t.duration) || 0}m</span>
                        <button className="btn ghost sm" style={{ padding: '3px 8px' }} onClick={() => setEditingEntry(t)}>Edit</button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                  <button
                    className="btn sm"
                    onClick={() => {
                      const staff = staffNameForUser(currentUser);
                      setEditingEntry({
                        id: 'r' + Date.now(),
                        pid: patient.id,
                        date: entryDateForMonth(month),
                        duration: 10,
                        desc: '',
                        label: '',
                        type: 'Other',
                        kind: 'manual',
                        ts: Date.now(),
                        staff,
                        staffUserId: currentUser?.id || '',
                        _isNew: true,
                      });
                    }}>
                    + Add entry
                  </button>
                  <button
                    className="btn sm ghost"
                    title="Open the audit log in the legacy UI"
                    onClick={() => window.open('/v2#audit', '_blank', 'noopener')}>
                    View source audit
                  </button>
                </div>
              </div>
            )}
            {!manualEdit && (
              <DraftAI
                patient={patient}
                tab={tab}
                draft={draft}
                month={month}
                onApplyRevision={canEditDocuments ? async (section, nextText) => {
                  if (section !== 'careplan') throw new Error('Only care plan revisions can be applied here.');
                  await ccmApi.saveCarePlan(patient.id, month, nextText);
                  invalidateDraftCache?.(patient.id, month);
                  setDraftRefreshKey(k => k + 1);
                  setToast({ id: Date.now(), text: `Care plan updated for ${patient.name}`, undo: null });
                } : null}
              />
            )}
          </div>

          <div className="preview-foot">
            <div className="nav-help">
              <span><span className="kbd">↑↓</span> next</span>
              <span><span className="kbd">Esc</span> close</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn" onClick={() => {
                const idx = filtered.findIndex(p => p.id === selectedId);
                if (idx > 0) setSelectedId(filtered[idx - 1].id);
              }}>← Prev</button>
              <button className="btn accent" onClick={() => {
                const idx = filtered.findIndex(p => p.id === selectedId);
                if (idx < filtered.length - 1) setSelectedId(filtered[idx + 1].id);
              }}>Next →<span className="kbd">↓</span></button>
            </div>
          </div>
          </div>
        </div>
      )}

      {settingsFor && (
        <OverridesModal
          patient={settingsFor}
          month={month}
          onClose={() => setSettingsFor(null)}
          onSaved={(nextRaw) => {
            // Re-adapt the row with the new _raw so derived fields (program,
            // consent, target, status) immediately reflect the override.
            setPatients(prev => prev.map(p => {
              if (p.id !== settingsFor.id) return p;
              const re = adaptPatient({ id: p.id, data: nextRaw }, month);
              return re || { ...p, _raw: nextRaw };
            }));
            invalidateDraftCache?.();
            setDraftRefreshKey(k => k + 1);
            setSettingsFor(null);
          }}
        />
      )}
      {editingEntry && patient && (
        <TimeEntryEditModal
          patient={patient}
          entry={editingEntry}
          currentUser={currentUser}
          onClose={() => setEditingEntry(null)}
          onSaved={(nextData) => {
            // Update local patient state so the Time-log list reflects the
            // change immediately, then force useDraft() to refetch by
            // invalidating its module cache + bumping the refresh key.
            setPatients(prev => prev.map(x => x.id === patient.id ? { ...x, _raw: nextData } : x));
            invalidateDraftCache?.();
            setDraftRefreshKey(k => k + 1);
            setEditingEntry(null);
          }}
        />
      )}
      {complexCcmReview && (
        <ComplexCcmReviewModal
          patient={complexCcmReview.patient}
          monthStr={complexCcmReview.monthStr}
          eligibility={complexCcmReview.eligibility}
          busy={complexCcmBusy}
          onClose={() => { if (!complexCcmBusy) setComplexCcmReview(null); }}
          onDecide={handleComplexCcmDecision}
        />
      )}
      {tierBumpReview && (
        <TierBumpReviewModal
          rows={tierBumpReview.rows}
          busy={tierBumpBusy}
          onClose={() => { if (!tierBumpBusy) setTierBumpReview(null); }}
          onApply={applyTierBumpAndGenerate}
        />
      )}
      {/* Inbox-launched tier review: same modal + write path, no generate. */}
      {inboxTierReview && (
        <TierBumpReviewModal
          rows={inboxTierReview.rows}
          busy={inboxTierBusy}
          onClose={() => { if (!inboxTierBusy) setInboxTierReview(null); }}
          onApply={applyInboxTierAffirm}
        />
      )}
      {bhiNoAssessmentReview && (
        <BhiNoAssessmentReviewModal
          patient={bhiNoAssessmentReview.patient}
          monthStr={bhiNoAssessmentReview.mStr}
          mode={bhiNoAssessmentReview.mode}
          busy={bhiNoAssessmentBusy}
          onClose={() => { if (!bhiNoAssessmentBusy) setBhiNoAssessmentReview(null); }}
          onProceed={handleBhiNoAssessmentProceed}
        />
      )}
      {painNoScaleReview && (
        <PainNoScaleReviewModal
          patient={painNoScaleReview.patient}
          monthStr={painNoScaleReview.mStr}
          mode={painNoScaleReview.mode}
          busy={painNoScaleBusy}
          onClose={() => { if (!painNoScaleBusy) setPainNoScaleReview(null); }}
          onProceed={handlePainNoScaleProceed}
        />
      )}
      {historyOpen && patient && (
        <BhiAssessmentHistoryModal
          patient={patient}
          onClose={() => setHistoryOpen(false)}
        />
      )}
      {showPush && (
        <PushModal
          tasks={pushTasks}
          month={month}
          onJumpToTime={onJumpToTimePatient}
          onJumpToApcm={onJumpToApcmPatient}
          onClose={() => {
            setShowPush(false);
            // Refresh draft cache + billedState so the chrome-bar count and
            // preview-header disabled state reflect what was just pushed.
            setDraftRefreshKey(k => k + 1);
          }}
          onDone={() => {
            // Re-fetch patients + billedState/billingNotes so workflow state
            // reflects what just happened. The signing step inside Push & Sign
            // already PATCH-signed atomically, so we no longer track per-
            // patient note ids client-side.
            ccmApi.listPatients()
              .then((rows) => setPatients((rows || []).filter(r => studioOperationalForFilter(r, programFilter)).map(r => adaptPatient(r, month)).filter(Boolean)))
              .catch(() => {});
            setDraftRefreshKey((k) => k + 1);
          }}
        />
      )}

      {/* Floating push button anchored to chrome — label flips between
          Generate and Push & Sign depending on workflow stage. */}
      <PushAnchor
        count={chromeWorkflow.stage === 'generate' ? chromeWorkflow.generateN : chromeWorkflow.pushSignN}
        isNarrow={isNarrow}
        month={monthLabel}
        label={chromeWorkflow.stage === 'pushsign' ? 'Push & Sign' : 'Generate'}
        onPush={() => setShowPush(true)}
      />

      {gateNotices.length > 0 && (
        <div className="studio-gate-notice" role="alert">
          <div>
            {gateNotices.map((w, i) => <div key={i}>⚠ {w}</div>)}
          </div>
          <button className="btn ghost sm" onClick={() => setGateNotices([])}>Dismiss</button>
        </div>
      )}

      {/* Inline quick-log toast */}
      {toast && (
        <div className={`studio-toast ${toast.error ? 'error' : ''}`} role="status">
          <span className="dot" aria-hidden="true"/>
          <span className="studio-toast-text">{toast.text}</span>
          {toast.undo && (
            <button className="studio-toast-undo" onClick={() => { toast.undo(); setToast(null); }}>Undo</button>
          )}
        </div>
      )}
    </div>
  );
}
